import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import MainLayout from '../../layouts/MainLayout';
import {
  getUnmatchedEmails,
  ignoreUnmatchedEmail,
} from '../../services/emailService';
import UnmatchedEmailFilters from './components/UnmatchedEmailFilters';
import UnmatchedEmailsList from './components/UnmatchedEmailsList';
import UnmatchedEmailPreview from './components/UnmatchedEmailPreview';
import AssignEmailModal from './components/AssignEmailModal';
import Pagination from '../../components/Pagination';
import { EnvelopeIcon } from '@heroicons/react/24/outline';

const UnmatchedEmailsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [emailToAssign, setEmailToAssign] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
  });

  const currentPage = parseInt(searchParams.get('page')) || 1;
  const userToken = localStorage.getItem('token');

  const fetchEmails = async () => {
    try {
      setLoading(true);

      const params = {
        page: currentPage,
        limit: 20,
        status: searchParams.get('status') || 'pending',
      };

      // La fase solo la elige el admin; el resto va a lo suyo por el ámbito
      // del backend, que no depende de este parámetro.
      const phase = searchParams.get('phase');
      if (phase) params.phase = phase;

      const search = searchParams.get('search');
      if (search) params.search = search;

      const startDate = searchParams.get('startDate');
      if (startDate) params.startDate = startDate;

      const endDate = searchParams.get('endDate');
      if (endDate) params.endDate = endDate;

      const response = await getUnmatchedEmails(params);
      setEmails(response.emails || []);
      setPagination(response.pagination || {});

      // Select first email if none selected
      if (response.emails?.length > 0 && !selectedEmail) {
        setSelectedEmail(response.emails[0]);
      }
    } catch (error) {
      console.error('Error fetching unmatched emails:', error);
      if (error.response?.status === 403) {
        window.location = '/dashboard';
      }
      toast.error('Error al cargar los emails');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!userToken) {
      window.location = '/login';
      return;
    }
    fetchEmails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, userToken]);

  const handlePageChange = page => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', page);
    setSearchParams(newParams);
  };

  const handleSelectEmail = email => {
    setSelectedEmail(email);
  };

  const handleOpenAssignModal = email => {
    setEmailToAssign(email);
  };

  const handleCloseAssignModal = () => {
    setEmailToAssign(null);
  };

  const handleEmailAssigned = emailId => {
    toast.success('Email asignado correctamente');
    setEmailToAssign(null);
    // Remove from list or refresh
    setEmails(prev => prev.filter(e => e._id !== emailId));
    setSelectedEmail(null);
    fetchEmails();
  };

  const handleIgnoreEmail = async emailId => {
    if (!window.confirm('¿Estás seguro de que quieres ignorar este email?')) {
      return;
    }

    try {
      await ignoreUnmatchedEmail(emailId);
      toast.success('Email marcado como ignorado');
      setEmails(prev => prev.filter(e => e._id !== emailId));
      setSelectedEmail(null);
      fetchEmails();
    } catch (error) {
      console.error('Error ignoring email:', error);
      toast.error('Error al ignorar el email');
    }
  };

  return (
    <MainLayout>
      {/* Page header */}
      <div className='sm:flex sm:items-center sm:justify-between mb-8'>
        <div className='mb-4 sm:mb-0'>
          <div className='flex items-center'>
            <div className='mr-4'>
              <EnvelopeIcon className='w-7 h-7 text-blue-600' />
            </div>
            <div>
              <h1 className='text-2xl font-semibold text-slate-900'>
                Emails sin asignar
              </h1>
              <p className='mt-1.5 text-sm text-slate-600'>
                Gestiona los emails entrantes que no se han podido vincular automáticamente
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className='bg-white shadow-lg rounded-lg border border-slate-200'>
        {/* Filters */}
        <div className='px-5 py-4 border-b border-slate-100'>
          <UnmatchedEmailFilters />
        </div>

        {/* Content */}
        <div className='flex min-h-[calc(100vh-350px)]'>
          {/* Email list */}
          <div className='w-1/3 border-r border-slate-200 overflow-y-auto'>
            {loading ? (
              <div className='text-center py-12'>
                <div className='inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500'></div>
                <p className='text-slate-500 mt-2'>Cargando...</p>
              </div>
            ) : emails.length === 0 ? (
              <div className='text-center py-12'>
                <EnvelopeIcon className='w-12 h-12 text-gray-300 mx-auto mb-3' />
                <p className='text-slate-500'>No hay emails pendientes</p>
              </div>
            ) : (
              <UnmatchedEmailsList
                emails={emails}
                selectedId={selectedEmail?._id}
                onSelect={handleSelectEmail}
              />
            )}
          </div>

          {/* Email preview */}
          <div className='flex-1 overflow-hidden'>
            <UnmatchedEmailPreview
              email={selectedEmail}
              onAssign={handleOpenAssignModal}
              onIgnore={handleIgnoreEmail}
            />
          </div>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className='px-5 py-4 border-t border-slate-100'>
            <div className='flex justify-between items-center'>
              <p className='text-sm text-gray-500'>
                Mostrando {emails.length} de {pagination.total} emails
              </p>
              <Pagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          </div>
        )}
      </div>

      {/* Assign Modal */}
      {emailToAssign && (
        <AssignEmailModal
          email={emailToAssign}
          onClose={handleCloseAssignModal}
          onAssigned={handleEmailAssigned}
        />
      )}
    </MainLayout>
  );
};

export default UnmatchedEmailsPage;
